import { skipToken } from "@reduxjs/toolkit/query";
import {
  checkFunnelUiVisible,
  checkFunnelUiWritable,
} from "@repo/app-types/entities/funnel";
import type { PlaybookId } from "@repo/app-types/entities/playbook";
import { FeatureFlagKey } from "@repo/app-types/featureFlags/keys";
import { usePlaybookFunnelsQuery } from "../../../api/entities/funnel.js";
import { useFeatureFlagVariant } from "../../../helpers/featureFlags/hooks.js";
import type { FunnelView } from "./useFunnelView.js";

/**
 * The Funnel section of a playbook: shown where funnels are writable, and
 * where they're read-only only on a playbook that has one, as the agent's
 * Funnel view is ({@link useFunnelView}). Hidden without a playbook,
 * including while the `default` placeholder resolves: pass
 * `usePlaybookIdParam()`, never the raw route param.
 */
export function usePlaybookFunnelView(
  playbookId: PlaybookId | undefined,
): FunnelView {
  const funnelUiVariant = useFeatureFlagVariant(
    FeatureFlagKey.VoiceAgentFunnelUi,
  );
  const isFunnelUiVisible =
    playbookId !== undefined && checkFunnelUiVisible(funnelUiVariant);
  const { data: funnels, isLoading } = usePlaybookFunnelsQuery(
    isFunnelUiVisible ? playbookId : skipToken,
  );
  const hasFunnel = Boolean(funnels?.length);
  const isShown =
    isFunnelUiVisible && (checkFunnelUiWritable(funnelUiVariant) || hasFunnel);
  return { isShown, isDefault: isShown && hasFunnel, isLoading };
}
